import React, { useEffect, useState } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';
import { AnnouncementLayout, UpdateBlock } from './AnnouncementLayout';

type UpdateItem = {
  id: string;
  title: string;
  body: string;
  audience: 'shareholder' | 'affiliate' | 'both';
  published_at?: string | null;
  language_code?: string;
  blocks?: UpdateBlock[];
  acknowledged: boolean;
  acknowledged_at?: string | null;
};

const AUDIENCE_LABEL = {
  shareholder: 'Shareholders',
  affiliate: 'Affiliates',
  both: 'Shareholders and affiliates',
};

function formatWhen(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
}

export const UpdatesPage: React.FC<{ audience: 'shareholder' | 'affiliate' }> = ({ audience }) => {
  const [updates, setUpdates] = useState<UpdateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [savingId, setSavingId] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    const headers = getSessionHeaders();
    if (!headers['X-Session-Id']) {
      setError('Sign in again to read updates.');
      setLoading(false);
      return;
    }
    try {
      const response = await fetch('/api/user/announcements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ operation: 'list', audience }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        setError(data?.error || 'We could not load the updates.');
        return;
      }
      setUpdates(data.announcements || []);
    } catch {
      setError('We could not load the updates.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [audience]);

  const confirm = async (id: string) => {
    if (!checked[id] || savingId) return;
    setSavingId(id);
    setError('');
    try {
      const headers = getSessionHeaders();
      const response = await fetch('/api/user/announcements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ operation: 'acknowledge', announcementId: id, audience, understood: true }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.success) {
        setError(data?.error || 'We could not save your confirmation. Please try again.');
        return;
      }
      await load();
    } catch {
      setError('We could not save your confirmation. Please try again.');
    } finally {
      setSavingId('');
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 sm:p-6">
      <h2 className="text-2xl font-bold text-white mb-2">Updates</h2>
      <p className="text-gray-300 mb-6">
        These are the published updates for this page, in the language you saved in Settings. English is used until you choose one.
      </p>
      {error ? <p className="text-red-300 mb-4">{error}</p> : null}
      {loading ? <p className="text-gray-300">Loading updates...</p> : null}
      {!loading && updates.length === 0 ? <p className="text-gray-300">No updates yet.</p> : null}
      <div className="space-y-4">
        {updates.map((item) => (
          <article key={item.id} className="border border-gray-700 rounded-lg p-4 bg-gray-900">
            <div className="flex flex-wrap items-center gap-2 mb-2 text-sm text-gray-400">
              <span>{AUDIENCE_LABEL[item.audience] || 'Update'}</span>
              {item.published_at ? <span>· {formatWhen(item.published_at)}</span> : null}
            </div>
            <h3 className="text-xl font-semibold text-yellow-400 mb-3">{item.title}</h3>
            <AnnouncementLayout blocks={item.blocks} fallback={item.body} />
            {item.acknowledged ? (
              <p className="mt-4 text-green-300">
                You confirmed that you read and understand this update{item.acknowledged_at ? ` on ${formatWhen(item.acknowledged_at)}` : ''}.
              </p>
            ) : (
              <div className="mt-4">
                <label className="flex items-start gap-3 text-gray-100 mb-3">
                  <input
                    type="checkbox"
                    checked={!!checked[item.id]}
                    onChange={(event) => setChecked((current) => ({ ...current, [item.id]: event.target.checked }))}
                    className="mt-1"
                  />
                  <span>I have read and understand this update.</span>
                </label>
                <button
                  type="button"
                  onClick={() => confirm(item.id)}
                  disabled={!checked[item.id] || savingId === item.id}
                  className="px-4 py-2 rounded-lg font-semibold bg-yellow-500 text-black disabled:bg-gray-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                >
                  {savingId === item.id ? 'Saving...' : 'Submit'}
                </button>
              </div>
            )}
          </article>
        ))}
      </div>
    </div>
  );
};

export default UpdatesPage;
